import React, { useEffect, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import { reduxForm, Field } from 'redux-form';

import Logo from '../../../../assets/img/sobre-rodas.png';
import validate from './validatePassword';

import './styles.css';
import UserRepository from '../../../../repositories/UserRepository';
import { getSessionUser } from '../../../../services/sessionService';
import { clearClientSession } from '../../../../services/session';
import { Label } from '../../../../components/Label';
import { Text } from '../../../../components/Text';
import { InputText } from '../../../../components/InputText';
import { useFirstAccess } from './useFirstAccess';
import { stripSensitiveParams } from '../../../../utils/urlToken';
import { Button } from '../../../../components/Button';

/**
 * Redirects the user if a valid session already exists (cookie checked by GET /session).
 * @param {object} history - The react-router history object.
 * @param {string|null} redirect - The redirect path from URL parameters.
 * @returns {Promise<boolean>} - True if a redirect was performed, false otherwise.
 */
const handleExistingSessionRedirect = async (history, redirect) => {
  let user = null;
  try {
    user = await getSessionUser();
  } catch (err) {
    return false;
  }
  if (!user) {
    clearClientSession();
    return false;
  }

  const USER_CONFIRMED = localStorage.getItem('USER_CONFIRMED');
  if (USER_CONFIRMED === 'false') {
    history.push('/client/confirmation-data');
    return true;
  }
  if (USER_CONFIRMED === 'true') {
    history.push(redirect ? `/client/${redirect}` : '/client/dashboard');
    return true;
  }
  return false;
};

let FirstClientAccess = ({ history, location }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { values, errors, handleChange, trigger } = useFirstAccess({});

  const [token, setToken] = useState('');
  const [redirect, setRedirect] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');

    let active = true;
    const start = async () => {
      const wasRedirected = await handleExistingSessionRedirect(
        history,
        redirectParam,
      );
      if (!active || wasRedirected) {
        return;
      }

      const linkToken = params.get('token');
      if (linkToken) {
        setToken(linkToken);
        stripSensitiveParams();
        if (redirectParam) {
          setRedirect(redirectParam);
        }
      } else {
        history.push('/client/login');
      }
    };
    start();
    return () => {
      active = false;
    };
  }, [history, location]);

  async function handleSubmit(e) {
    e.preventDefault();
    const isValidFields = await trigger(['password', 'confirmPassword']);

    if (!isValidFields) {
      toastr.error('Erro', 'Por favor, corrija os erros antes de prosseguir.');
      return;
    }
    setIsLoading(true);
    try {
      await UserRepository.updatePassword({
        token,
        password: values.password,
      });
      toastr.success('Senha salva com sucesso!', 'Faça login para continuar.');
      const query = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      history.push(`/client/login${query}`);
    } catch (err) {
      const message = err.response?.data?.message || 'Tente novamente.';
      if (message.includes('A nova senha deve ser diferente da senha antiga.')) {
        return toastr.warning('Houve um erro ao salvar senha', message);
      }
      return toastr.error('Houve um erro ao salvar senha', message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <div className="Login-container">
        <div className="Login">
          <div className="Login-card">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                textAlign: 'center',
                alignItems: 'center',
              }}
            >
              <img className="img" src={Logo} alt="Sobre Rodas" />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                textAlign: 'center',
                marginBottom: '20px',
                alignItems: 'center',
              }}
            >
              <span style={{ marginTop: '10px' }}>
                <strong>Cadastrar senha de acesso</strong>
              </span>
            </div>
            <form>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <Label required>Senha</Label>
                <Text color="red400" size="xs">
                  Mín. 6 caracteres
                </Text>
              </div>
              <InputText
                name="password"
                value={values.password}
                onChange={(e) => handleChange('password', e.target.value)}
                onBlur={() => trigger(['password'])}
                type="password"
                isLoading={isLoading}
                error={errors.password}
              />

              <Label required>Confirme a Senha</Label>
              <InputText
                name="confirmPassword"
                value={values.confirmPassword}
                onChange={(e) =>
                  handleChange('confirmPassword', e.target.value)
                }
                onBlur={() => trigger(['confirmPassword'])}
                type="password"
                isLoading={isLoading}
                error={errors.confirmPassword}
              />
              <div
                style={{
                  display: 'flex',
                  marginTop: '10px',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  textAlign: 'center',
                }}
              >
                <Button
                  color="green450"
                  onClick={(e) => handleSubmit(e)}
                  disabled={
                    !values.password ||
                    !values.confirmPassword ||
                    values.password.length < 6 ||
                    isLoading
                  }
                >
                  Cadastrar senha
                  {isLoading && (
                    <span
                      className="fa fa-spinner fa-pulse fa-1x"
                      style={{ marginLeft: '5px' }}
                    />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

FirstClientAccess = reduxForm({
  destroyOnUnmount: false, //        <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  form: 'firstClientAccess',
  validate,
})(FirstClientAccess);

export default FirstClientAccess;
